import { useState, useEffect } from 'react';
import { Search, Users, Mail, Phone, ShoppingBag, DollarSign, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);
const statusLabels = { pending: 'Pendiente', approved: 'Aprobado', production: 'Produccion', delivery: 'Entrega', delivered: 'Entregado', cancelled: 'Cancelado' };
const statusColors = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-blue-100 text-blue-800', production: 'bg-purple-100 text-purple-800', delivery: 'bg-orange-100 text-orange-800', delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOrders, setClientOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await axios.get(`${API}/admin/clients`, { withCredentials: true, headers: getHeaders(), params });
      setClients(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchClients(); }, []);

  const viewClient = async (client) => {
    setSelectedClient(client);
    try {
      const { data } = await axios.get(`${API}/admin/clients/${encodeURIComponent(client.email)}/orders`, { withCredentials: true, headers: getHeaders() });
      setClientOrders(data);
    } catch { setClientOrders([]); }
    setDialogOpen(true);
  };

  const handleSearch = (e) => { e.preventDefault(); fetchClients(); };

  const totalSpent = clients.reduce((s, c) => s + (c.total_spent || 0), 0);

  return (
    <div data-testid="admin-clients-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-[#46371c]">Clientes</h1>
        <Badge className="bg-[#a0d5ba]/20 text-[#46371c] border-[#a0d5ba] text-sm">{clients.length} clientes</Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="clients-stats">
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#a0d5ba]/20 flex items-center justify-center"><Users className="w-5 h-5 text-[#46371c]" /></div>
            <div><p className="font-body text-xs text-[#75664d]">Total Clientes</p><p className="font-heading text-2xl font-bold text-[#46371c]">{clients.length}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-blue-700" /></div>
            <div><p className="font-body text-xs text-[#75664d]">Total Pedidos</p><p className="font-heading text-2xl font-bold text-[#46371c]">{clients.reduce((s, c) => s + (c.total_orders || 0), 0)}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-[#e5dfd3] bg-[#fcfbf9]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center"><DollarSign className="w-5 h-5 text-green-700" /></div>
            <div><p className="font-body text-xs text-[#75664d]">Ingresos Totales</p><p className="font-heading text-2xl font-bold text-[#46371c]">{formatPrice(totalSpent)}</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75664d]" />
          <input data-testid="clients-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, email o telefono..."
            className="w-full h-10 pl-9 pr-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" />
        </div>
        <Button type="submit" className="bg-[#46371c] text-[#fcfbf9] rounded-xl h-10 font-body" data-testid="clients-search-btn">Buscar</Button>
      </form>

      {/* Table */}
      <Card className="border border-[#e5dfd3] bg-[#fcfbf9] overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
        ) : clients.length === 0 ? (
          <p className="text-center py-12 font-body text-sm text-[#75664d]">No hay clientes registrados</p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[650px]">
              <TableHeader>
                <TableRow className="bg-[#f4f1ea]">
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Cliente</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Contacto</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Pedidos</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Total Gastado</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Registro</TableHead>
                  <TableHead className="font-body text-xs font-semibold text-[#46371c]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map(client => (
                  <TableRow key={client.email} data-testid={`client-row-${client.email}`}>
                    <TableCell>
                      <p className="font-body text-sm font-medium text-[#46371c]">{client.name || 'Sin nombre'}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <p className="font-body text-xs text-[#75664d] flex items-center gap-1"><Mail className="w-3 h-3" />{client.email}</p>
                        {client.phone && <p className="font-body text-xs text-[#75664d] flex items-center gap-1"><Phone className="w-3 h-3" />{client.phone}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-body text-sm font-semibold text-[#46371c]">{client.total_orders || 0}</span>
                        {client.delivered_orders > 0 && <Badge className="bg-green-100 text-green-700 text-xs">{client.delivered_orders} entregados</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="font-body text-sm font-semibold text-[#46371c]">{formatPrice(client.total_spent || 0)}</TableCell>
                    <TableCell className="font-body text-xs text-[#75664d]">{client.created_at ? new Date(client.created_at).toLocaleDateString('es-CO') : '-'}</TableCell>
                    <TableCell>
                      <button onClick={() => viewClient(client)} className="p-2 hover:bg-[#f4f1ea] rounded-lg transition-colors" data-testid={`view-client-${client.email}`}>
                        <Eye className="w-4 h-4 text-[#75664d]" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Client Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#fcfbf9]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">{selectedClient?.name || 'Cliente'}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">{selectedClient?.email}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-[#f4f1ea] p-3 rounded-xl text-center">
              <p className="font-body text-xs text-[#75664d]">Pedidos</p>
              <p className="font-heading text-xl font-bold text-[#46371c]">{selectedClient?.total_orders || 0}</p>
            </div>
            <div className="bg-[#eef6f2] p-3 rounded-xl text-center">
              <p className="font-body text-xs text-[#75664d]">Entregados</p>
              <p className="font-heading text-xl font-bold text-[#46371c]">{selectedClient?.delivered_orders || 0}</p>
            </div>
            <div className="bg-[#a0d5ba]/10 p-3 rounded-xl text-center">
              <p className="font-body text-xs text-[#75664d]">Total</p>
              <p className="font-heading text-xl font-bold text-[#46371c]">{formatPrice(selectedClient?.total_spent || 0)}</p>
            </div>
          </div>
          <h4 className="font-heading text-lg font-semibold text-[#46371c] mb-2">Historial de pedidos</h4>
          {clientOrders.length === 0 ? (
            <p className="font-body text-sm text-[#75664d] text-center py-4">Sin pedidos</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {clientOrders.map(o => (
                <div key={o.id} className="flex items-center justify-between p-3 rounded-xl bg-[#f4f1ea]">
                  <div>
                    <p className="font-body text-sm font-medium text-[#46371c]">{o.tracking_code}</p>
                    <p className="font-body text-xs text-[#75664d]">{new Date(o.created_at).toLocaleDateString('es-CO')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-body text-sm font-semibold text-[#46371c]">{formatPrice(o.total)}</span>
                    <Badge className={`text-xs ${statusColors[o.status] || ''}`}>{statusLabels[o.status] || o.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
